<template>
  <div class="min-h-screen bg-gray-50">
    <AppNavbar />

    <!-- Profile Header -->
    <div class="bg-indigo-700 text-white pt-10 pb-6">
      <div class="max-w-7xl mx-auto">
        <div class="flex flex-col md:flex-row items-center justify-between">
          <div class="flex items-center space-x-4">
            <div class="relative">
              <div
                class="h-24 w-24 rounded-full overflow-hidden border-2 border-white/30"
              >
                <img
                  v-if="user.image"
                  :src="getImageUrl(user.image)"
                  alt="User Image"
                  class="w-full h-full object-cover"
                />
                <div
                  v-else
                  class="w-full h-full bg-white/20 backdrop-blur-sm flex items-center justify-center"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    class="h-12 w-12 text-white"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="1.5"
                      d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                    />
                  </svg>
                </div>
              </div>
            </div>
            <div>
              <h1 class="text-2xl font-bold text-white">{{ user.name }}</h1>
              <p class="text-blue-100">{{ user.email }}</p>
            </div>
          </div>

          <div class="mt-4 md:mt-0 flex space-x-3">
            <button
              @click="activeTab = 'profile'"
              class="px-4 py-2 rounded-full text-sm font-medium transition-all"
              :class="
                activeTab === 'profile'
                  ? 'bg-white text-blue-600 shadow-md'
                  : 'text-white hover:bg-white/20'
              "
            >
              {{ $t("profile.profileSettings") }}
            </button>
            <button
              @click="activeTab = 'items'"
              class="px-4 py-2 rounded-full text-sm font-medium transition-all"
              :class="
                activeTab === 'items'
                  ? 'bg-white text-blue-600 shadow-md'
                  : 'text-white hover:bg-white/20'
              "
            >
              {{ $t("profile.myItems") }}({{ items.length }})
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Main Content -->
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <!-- Profile Settings -->
      <div
        v-if="activeTab === 'profile'"
        class="bg-white rounded-xl shadow-sm overflow-hidden border border-gray-100"
      >
        <div class="px-6 py-5 border-b border-gray-100">
          <h2 class="text-lg font-semibold text-gray-800">
            {{ $t("profile.personalInformation") }}
          </h2>
        </div>
        <div class="p-6">
          <form @submit.prevent="updateProfile">
            <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">
                  {{ $t("profile.fullName") }}</label
                >
                <input
                  v-model="form.name"
                  type="text"
                  class="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">{{
                  $t("profile.email")
                }}</label>
                <input
                  v-model="form.email"
                  type="email"
                  class="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>
              <!-- Current Password -->
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">{{
                  $t("profile.currentPassword")
                }}</label>
                <input
                  v-model="form.old_password"
                  type="password"
                  autocomplete="current-password"
                  class="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <!-- New Password -->
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">{{
                  $t("profile.newPassword")
                }}</label>
                <input
                  v-model="form.new_password"
                  type="password"
                  autocomplete="new-password"
                  class="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <!-- Confirm New Password -->
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">{{
                  $t("profile.confirmPassword")
                }}</label>
                <input
                  v-model="form.new_password_confirmation"
                  type="password"
                  autocomplete="new-password"
                  class="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <!-- Profile Image Upload -->
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">
                  {{ $t("profile.profileImage") }}
                </label>
                <input
                  type="file"
                  @change="handleImageUpload"
                  class="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />
              </div>
            </div>

            <div class="mt-8 flex justify-end space-x-3">
              <!-- <button
                type="button"
                @click="resetForm"
                class="px-4 py-2.5 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
              >
                {{ $t("profile.cancel") }}
              </button> -->
              <button
                type="submit"
                class="px-6 py-2.5 bg-teal-500 text-white rounded-lg shadow-md hover:shadow-lg transition-all"
              >
                {{ $t("profile.saveChange") }}
              </button>
            </div>
          </form>
        </div>
      </div>

      <!-- My Items -->
      <div v-else>
        <div class="flex justify-between items-center mb-6">
          <h2 class="text-xl font-bold text-gray-800">
            {{ $t("profile.myItems") }}
          </h2>
          <button
            @click="openAddProductModal"
            class="inline-flex items-center px-5 py-2.5 bg-gradient-to-r from-teal-500 to-emerald-500 text-white text-sm font-semibold rounded-lg shadow-md hover:from-teal-600 hover:to-emerald-600 transition-all duration-300"
          >
            <svg
              class="h-5 w-5 mr-2"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M12 4v16m8-8H4"
              ></path>
            </svg>
            {{ $t("profile.addNewItem") }}
          </button>
        </div>

        <div
          v-if="loading"
          class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6"
        >
          <div
            v-for="n in 6"
            :key="n"
            class="bg-white rounded-lg shadow-sm p-4"
          >
            <div class="animate-pulse">
              <div class="h-48 bg-gray-200 rounded-lg mb-4"></div>
              <div class="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
              <div class="h-4 bg-gray-200 rounded w-1/2"></div>
            </div>
          </div>
        </div>

        <div
          v-else-if="items.length"
          class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6"
        >
          <div
            v-for="item in items"
            :key="item.id"
            class="bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow overflow-hidden"
          >
            <!-- Item Images Slider -->
            <div class="relative h-48 overflow-hidden">
              <router-link :to="`/product/${item.id}`" class="block h-full">
                <div
                  class="flex h-full transition-transform duration-300"
                  :style="`transform: translateX(-${
                    item.currentImageIndex * 100
                  }%)`"
                >
                  <img
                    v-for="(img, index) in getItemImages(item)"
                    :key="index"
                    :src="getImageUrl(img)"
                    class="w-full h-full object-cover flex-shrink-0"
                    :alt="item.title"
                  />
                  <div
                    v-if="!getItemImages(item).length"
                    class="w-full h-full bg-gray-100 flex items-center justify-center"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      class="h-12 w-12 text-gray-400"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="1"
                        d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                      />
                    </svg>
                  </div>
                </div>
              </router-link>

              <!-- Navigation Arrows -->
              <button
                v-if="getItemImages(item).length > 1"
                @click.stop="prevImage(item)"
                class="absolute left-2 top-1/2 -translate-y-1/2 p-1 bg-white/80 rounded-full shadow hover:bg-white transition"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  class="h-5 w-5"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fill-rule="evenodd"
                    d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z"
                    clip-rule="evenodd"
                  />
                </svg>
              </button>
              <button
                v-if="getItemImages(item).length > 1"
                @click.stop="nextImage(item)"
                class="absolute right-2 top-1/2 -translate-y-1/2 p-1 bg-white/80 rounded-full shadow hover:bg-white transition"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  class="h-5 w-5"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fill-rule="evenodd"
                    d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                    clip-rule="evenodd"
                  />
                </svg>
              </button>

              <!-- Edit/Delete Buttons -->
              <div class="absolute top-2 right-2 flex space-x-2">
                <button
                  @click.stop="toggleStatus(item)"
                  class="p-2 rounded-full shadow hover:bg-white transition"
                  :class="
                    item.status === 1
                      ? 'bg-green-100 text-green-700'
                      : 'bg-red-100 text-red-700'
                  "
                  :title="item.status === 1 ? 'Set Inactive' : 'Set Active'"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    class="h-5 w-5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                </button>
                <router-link
                  :to="`/edit-product/${item.id}`"
                  class="p-2 bg-white/80 rounded-full shadow hover:bg-white transition"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    class="h-5 w-5 text-blue-600"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z"
                    />
                  </svg>
                </router-link>
                <button
                  @click.stop="confirmDelete(item)"
                  class="p-2 bg-white/80 rounded-full shadow hover:bg-white transition"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    class="h-5 w-5 text-red-600"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fill-rule="evenodd"
                      d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z"
                      clip-rule="evenodd"
                    />
                  </svg>
                </button>
              </div>
            </div>
            <div class="mt-2">
              <span
                class="inline-block px-2 py-0.5 rounded-full text-xs font-semibold"
                :class="
                  item.status === 1
                    ? 'bg-green-100 text-green-700'
                    : 'bg-red-100 text-red-700'
                "
              >
                {{ item.status === 1 ? "Active" : "Inactive" }}
              </span>
            </div>
            <!-- Item Approval Badge -->
            <div class="mt-1">
              <span
                class="inline-block px-2 py-0.5 rounded-full text-xs font-semibold"
                :class="
                  item.approved === 1
                    ? 'bg-blue-100 text-blue-700'
                    : 'bg-yellow-100 text-yellow-700'
                "
              >
                {{ item.approved === 1 ? "Approved" : "Pending Approval" }}
              </span>
            </div>

            <!-- Item Details -->
            <div class="p-4">
              <router-link :to="`/product/${item.id}`">
                <h3
                  class="font-semibold text-gray-800 hover:text-blue-600 mb-1"
                >
                  {{ item.title }}
                </h3>
              </router-link>

              <div class="flex items-center justify-between mt-2">
                <div>
                  <span
                    class="text-lg font-bold text-teal-600 inline-flex items-center gap-1"
                  >
                    {{ formatPrice(item.price) }}
                    <img src="/R.svg" alt="SAR" class="w-4 h-4" />
                  </span>
                </div>
                <div class="text-sm text-gray-500">
                  {{ formatDate(item.created_at) }}
                </div>
              </div>

              <!-- Item Fields -->
              <div class="mt-3 pt-3 border-t border-gray-100">
                <div
                  v-for="field in item.fields"
                  :key="field.label"
                  class="flex text-sm mb-1"
                >
                  <span class="text-gray-600 font-medium w-1/3"
                    >{{ field.label }}:</span
                  >
                  <span class="text-gray-800">
                    {{ formatFieldValue(field.value) }}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div v-else class="bg-white rounded-lg shadow-sm p-8 text-center">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            class="h-16 w-16 mx-auto text-gray-400"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="1"
              d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"
            />
          </svg>
          <h3 class="mt-4 text-lg font-medium text-gray-900">
            {{ $t("editProduct.noItemsFound") }}
          </h3>
          <p class="mt-1 text-sm text-gray-500">
            {{ $t("editProduct.getStarted") }}
          </p>
          <!-- <div class="mt-6">
            <router-link
              to="/add-item"
              class="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="-ml-1 mr-2 h-5 w-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                />
              </svg>
              {{ $t("profile.addNewItem") }}
            </router-link>
          </div> -->
        </div>
      </div>
    </div>
    <DynamicAddModal
      v-if="showAddModal"
      :title="$t('Add New Product')"
      :fields="staticFields"
      :initialForm="initialFormData"
      @submit="submitNewProduct"
      @close="showAddModal = false"
    />
  </div>
</template>

<script>
import Swal from "sweetalert2";
import api from "@/api";
import DynamicAddModal from "@/components/DynamicAddModal.vue";
export default {
  name: "UserProfile",
  components: {
    DynamicAddModal,
  },
  data() {
    return {
      isSubmitting: false,
      activeTab: "profile",
      loading: false,
      form: {
        name: "",
        email: "",
        old_password: "",
        new_password: "",
        new_password_confirmation: "",
      },
      imageFile: null,
      user: {
        name: "",
        email: "",
      },
      items: [],
      showAddModal: false,
      staticFields: [
        {
          model: "title",
          label: this.$t("products.addModal.name"),
          type: "text",
          required: true,
        },
        {
          model: "price",
          label: this.$t("products.addModal.price"),
          type: "number",
          required: true,
          priceIcon: true,
        },
        {
          model: "description",
          label: this.$t("products.addModal.description"),
          type: "textarea",
          required: false,
        },
      ],
      initialFormData: {
        title: "",
        description: "",
        price: 0,
      },
    };
  },
  created() {
    this.fetchUser();
    this.fetchItems();

    const tabFromRoute = this.$route.query.tab;
    if (tabFromRoute === "items" || tabFromRoute === "profile") {
      this.activeTab = tabFromRoute;
    }

    if (this.$route.query.openAddModal === "1") {
      this.showAddModal = true;
      // this.fetchCurrencies();
      this.$router.replace({
        query: { ...this.$route.query, openAddModal: undefined },
      });
    }
  },
  watch: {
    "$route.query.openAddModal"(val) {
      if (val === "1") {
        this.activeTab = "items"; // optional if not already on that tab
        this.showAddModal = true;
        this.$router.replace({
          query: { ...this.$route.query, openAddModal: undefined },
        });
      }
    },
  },
  methods: {
    handleImageUpload(event) {
      const file = event.target.files[0];
      if (file) {
        this.imageFile = file;
      }
    },
    formatFieldValue(value) {
      try {
        if (typeof value === "string" && value.startsWith("[")) {
          const parsed = JSON.parse(value);
          if (Array.isArray(parsed)) {
            return parsed.join(", ");
          }
        }

        if (Array.isArray(value)) {
          return value.join(", ");
        }

        return value;
      } catch (e) {
        return value;
      }
    },
    async toggleStatus(item) {
      try {
        const newStatus = item.status === 1 ? 0 : 1;
        const res = await api.post(`v1/items/${item.id}/status`, {
          status: newStatus,
        });

        if (res.data.success) {
          item.status = res.data.status; // update local item status
          alert(res.data.message || "Status updated successfully!");
        } else {
          alert("Failed to update status.");
        }
      } catch (error) {
        console.error("Error updating status:", error);
        alert(
          error.response?.data?.message ||
            "An error occurred while updating status."
        );
      }
    },
    async fetchUser() {
      try {
        const res = await api.get("/profile");
        this.user = res.data.user;
        localStorage.setItem(
          "user",
          JSON.stringify({
            name: this.user.name,
            email: this.user.email,
            image: this.user.image,
          })
        );
        this.form.name = res.data.user.name;
        this.form.email = res.data.user.email;
      } catch (e) {
        console.error("Failed to fetch user:", e);
      }
    },
    async fetchItems() {
      this.loading = true;
      try {
        const res = await api.get("/profile/items");

        // Transform items to include currentImageIndex for each
        this.items = (res.data.items || res.data).map((item) => ({
          ...item,
          currentImageIndex: 0,
        }));
      } catch (e) {
        console.error("Failed to fetch items:", e);
        this.items = [];
      } finally {
        this.loading = false;
      }
    },
    getItemImages(item) {
      if (!item.image) return [];
      if (Array.isArray(item.image)) return item.image;
      try {
        return JSON.parse(item.image);
      } catch {
        return [item.image];
      }
    },
    getImageUrl(imgPath) {
      if (!imgPath) return "";
      if (imgPath.startsWith("http")) return imgPath;
      return `https://admin.from-malk.com/storage/${imgPath}`;
    },
    prevImage(item) {
      const images = this.getItemImages(item);
      item.currentImageIndex =
        (item.currentImageIndex - 1 + images.length) % images.length;
    },
    nextImage(item) {
      const images = this.getItemImages(item);
      item.currentImageIndex = (item.currentImageIndex + 1) % images.length;
    },
    formatPrice(price) {
      return parseFloat(price).toFixed(2);
    },
    formatDate(dateString) {
      if (!dateString) return "";
      const date = new Date(dateString);
      return date.toLocaleDateString();
    },
    async updateProfile() {
      try {
        const formData = new FormData();
        formData.append("name", this.form.name);
        formData.append("email", this.form.email);
        if (this.form.old_password)
          formData.append("old_password", this.form.old_password);
        if (this.form.new_password)
          formData.append("new_password", this.form.new_password);
        if (this.form.new_password_confirmation) {
          formData.append(
            "new_password_confirmation",
            this.form.new_password_confirmation
          );
        }

        if (this.imageFile) {
          formData.append("image", this.imageFile);
        }

        const res = await api.post("/profile/update", formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });

        this.user = res.data.user;
        this.imageFile = null;
        this.resetForm();

        alert(
          this.$i18n.locale === "ar"
            ? "تم تحديث الملف الشخصي بنجاح"
            : "Profile updated successfully!"
        );
      } catch (e) {
        alert(e.response?.data?.message || "Error updating profile");
      }
    },
    resetForm() {
      this.form.old_password = "";
      this.form.new_password = "";
      this.form.new_password_confirmation = "";
    },
    confirmDelete(item) {
      if (confirm(`Are you sure you want to delete "${item.title}"?`)) {
        this.deleteItem(item.id);
      }
    },
    async deleteItem(id) {
      try {
        await api.post(`/v1/items/${id}/delete`);
        this.items = this.items.filter((item) => item.id !== id);
      } catch (e) {
        alert("Failed to delete item");
      }
    },

    openAddProductModal() {
      this.showAddModal = true;
      // this.fetchCurrencies();
    },

    async submitNewProduct(formData) {
      // guard: only proceed if there's at least one image in the FormData
      let hasImages = false;

      if (formData && typeof formData.entries === "function") {
        for (const [key, val] of formData.entries()) {
          if (key === "images" || key.startsWith("images[")) {
            if (val) {
              hasImages = true;
              break;
            } // File/Blob present
          }
        }
      } else if (formData && typeof formData === "object") {
        // fallback if someone passed a plain object by mistake
        hasImages = Object.keys(formData).some(
          (k) => k === "images" || /^images\[\d+\]$/.test(k)
        );
      }

      if (!hasImages) {
        console.warn("No images selected, skipping API call.");
        return;
      }

      if (this.isSubmitting) return;
      this.isSubmitting = true;
      try {
        await api.post("/v1/items", formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });
        this.showAddModal = false;
        await this.fetchItems(); // Refresh the items list
        this.$router.push({ name: "userProfile", query: { tab: "items" } });
      } catch (err) {
        if (err.response && err.response.status === 429) {
          const lang = this.$i18n.locale;
          const msg =
            lang === "ar"
              ? err.response.data.message_ar
              : err.response.data.message_en;

          Swal.fire({
            icon: "warning",
            title: msg,
            confirmButtonText: lang === "ar" ? "حسنًا" : "OK",
            customClass: {
              popup: "rounded-lg p-6", // optional: round and padding
              confirmButton:
                "bg-teal-500 hover:bg-teal-600 text-white font-bold py-2 px-4 rounded",
            },
            buttonsStyling: false,
          });

          return;
        }

        console.error("Failed to save product", err);
        Swal.fire({
          icon: "error",
          title:
            this.$i18n.locale === "ar"
              ? "حدث خطأ أثناء الحفظ"
              : "Failed to save product",
          confirmButtonText: this.$i18n.locale === "ar" ? "إغلاق" : "Close",
        });
      } finally {
        this.isSubmitting = false;
      }
    },
  },
};
</script>

<style scoped>
/* Smooth transitions for image slider */
.flex {
  transition: transform 0.3s ease;
}
</style>
